import React, { useState } from "react";
import {
    Box,
    Grid,
    Typography,
    SxProps,
    Theme,
    Card,
    CardContent,
    CardActions,
    IconButton,
    FormControl,
    Select,
    MenuItem,
    CardActionArea,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import HelperBox from "../components/HelperBox";
import { usePosition, useIsMobile } from "../hooks";
import { CommonStyles } from "../styles/CommonStyles";
import ArrowDetailIcon from "../components/icons/ArrowDetailIcon";
import JobIcon from "../components/icons/JobIcon";
import LocationIcon from "../components/icons/LocationIcon";
import { JobPositionPreview } from "../adapters/api";
import PageTitle from "../components/PageTitle";
import PageContent from "../components/PageContent";
import { useTranslation } from "react-i18next";

export type CareerPositionCardProps = {
    item: JobPositionPreview;
    onLearnMore: () => void;
};

const CareerPositionCard: React.FC<CareerPositionCardProps> = ({
    item,
    onLearnMore,
}) => {
    const { isMobile } = useIsMobile();
    const [hover, setHover] = useState(false);
    return (
        <Card
            sx={[
                isMobile
                    ? CommonStyles.cardContainerMobile
                    : CommonStyles.cardContainer,
                { height: isMobile ? "150px" : "100%", width: "100%" },
            ].flat(2)}
        >
            <CardActionArea
                onClick={onLearnMore}
                onPointerEnter={() => {
                    setHover(true);
                }}
                onPointerLeave={() => {
                    setHover(false);
                }}
            >
                <CardContent sx={{ padding: "20px" }}>
                    <Typography
                        sx={[
                            isMobile ? CommonStyles.cardTitleMobile : CommonStyles.cardTitle,
                            { textAlign: "left" },
                        ].flat(2)}
                    >
                        {item.title}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            marginBottom: "5px",
                            marginLeft: "15px",
                        }}
                    >
                        <JobIcon sx={{ marginRight: "10px" }} />
                        <Typography
                            sx={
                                isMobile
                                    ? CommonStyles.cardSubtitleMobile
                                    : CommonStyles.cardSubtitle
                            }
                        >
                            {item.employment}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            marginLeft: "15px",
                        }}
                    >
                        <LocationIcon sx={{ marginRight: "10px" }} />
                        <Typography
                            sx={
                                isMobile
                                    ? CommonStyles.cardSubtitleMobile
                                    : CommonStyles.cardSubtitle
                            }
                        >
                            {item.location}
                        </Typography>
                    </Box>
                </CardContent>
                <CardActions
                    sx={
                        isMobile
                            ? {
                                position: "absolute",
                                bottom: "0px",
                                right: "0px",
                            }
                            : {
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                            }
                    }
                >
                    <IconButton onClick={onLearnMore}>
                        <ArrowDetailIcon
                            sx={{
                                fontSize: "30px",
                                color: hover ? "secondary.main" : "#bababa",
                            }}
                        />
                    </IconButton>
                </CardActions>
            </CardActionArea>
        </Card>
    );
};

const CareersJobPositionPageStyle: Record<string, SxProps<Theme>> = {
    filterContainer: {
        textAlign: "left",
        marginBottom: "20px",
    },
    menuItem: (theme: Theme) => ({
        background: theme.palette.primary.main,
        "&.Mui-selected": { backgroundColor: theme.palette.secondary.main },
        border: `1px solid ${theme.palette.secondary.main}`,
        borderTop: "0px",
        "&:hover": {
            background: "#36314945",
        },
    }),
    menuList: (theme: Theme) => ({
        background: theme.palette.primary.main,
        color: "#bababa",
        borderTop: "0px",
        paddingTop: "2px",
        paddingBottom: "2px",
    }),
    selectInput: (theme: Theme) => ({
        color: "white",
        ".MuiSvgIcon-root ": {
            fill: `${theme.palette.secondary.main} !important`,
        },
        ".MuiSelect-select": {
            border: `1px solid ${theme.palette.secondary.main}`,
        },
        ".MuiMenuItem-root:hover": {
            border: `1px solid ${theme.palette.secondary.main}`,
        },
        ".MuiSelect-select:hover": {},
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "white",
        },
    }),
};

const CareersJobPositionPage = () => {
    const { isMobile } = useIsMobile();
    const { t } = useTranslation();

    let navigate = useNavigate();
    const { positions } = usePosition();
    const [locationFilter, setLocationFilter] = useState("");

    const locations = (): string[] => {
        let locationsList: string[] = [];
        positions.forEach((pos) => {
            if (!locationsList.includes(pos.location)) {
                locationsList.push(pos.location);
            }
        });
        return locationsList;
    };

    const filteredPositions = () => {
        if (locationFilter === "") {
            return positions;
        }
        return positions.filter((pos) => pos.location.includes(locationFilter));
    };

    return (
        <>
            <PageTitle>{t("careers.title")}</PageTitle>
            <PageContent>
                <Grid container sx={{ alignItems: "center" }}>
                    <Grid md={12} sx={CareersJobPositionPageStyle.filterContainer}>
                        <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
                            <Select
                                value={locationFilter}
                                onChange={(e) => setLocationFilter(e.target.value)}
                                displayEmpty
                                sx={CareersJobPositionPageStyle.selectInput}
                                inputProps={{
                                    "aria-label": "Without label",
                                    MenuProps: {
                                        MenuListProps: {
                                            sx: CareersJobPositionPageStyle.menuList,
                                        },
                                    },
                                }}
                                color="secondary"
                            >
                                <MenuItem sx={CareersJobPositionPageStyle.menuItem} value="">
                                    <span style={{ color: "white" }}>{t("careers.byloc")}</span>
                                </MenuItem>
                                {locations().map((loc) => (
                                    <MenuItem
                                        sx={CareersJobPositionPageStyle.menuItem}
                                        value={loc}
                                    >
                                        <span style={{ color: "white" }}>{loc}</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {
                        positions.length === 0 ? 
                        <Box width={"100%"}>
                            <HelperBox
                                text={t("careers.nopositions")}
                                buttonText={t("careers.apply")}
                                onClick={() => {
                                    // il link mailto ha integrato un oggetto diverso in base alla lingua
                                    window.location.assign((t("careers.mailto")).toString());
                                }}
                            />
                        </Box> :
                    

                    <Box sx={!isMobile ? { width: "100%", overflow: "auto", border: "1px solid red" } : {border: "1px solid red"}}>
                        <Grid
                            md={12}
                            sm={12}
                            container
                            spacing={isMobile ? 2 : 8}
                            sx={{ display: "flex" }}
                        >
                            {
                                filteredPositions().map((position) => (
                                <Grid
                                    item
                                    md={6}
                                    sm={12}
                                    lg={4}
                                    alignItems="stretch"
                                    sx={{ width: "100%" }}
                                >
                                    <CareerPositionCard
                                        item={position}
                                        onLearnMore={() => navigate(`${position.id}/detail`)}
                                    />
                                </Grid>
                            ))
                            }
                        </Grid>
                    </Box>
                    }
                </Grid>
                
                {
                    positions.length > 0 && <Grid
                        container
                        md={12}
                        sx={{ alignItems: "center", marginTop: "20px" }}
                    >
                        <Grid md={2} sm={0} />
                        <Grid md={8} sm={12}>
                            <HelperBox
                                text={t("careers.spont")}
                                buttonText={t("careers.apply")}
                                onClick={() => {
                                    // il link mailto ha integrato un oggetto diverso in base alla lingua
                                    window.location.assign((t("careers.mailto")).toString());
                                }}
                            />
                        </Grid>
                        <Grid md={2} sm={0} />
                    </Grid>
                }
            </PageContent>
        </>
    );
};

export default CareersJobPositionPage;
